/**
 * Ключ Яндекс Карт из пула на сервере (`GET /maps/key`) вместо ключа в сборке.
 *
 * Один ключ — дневной лимит запросов. Сервер держит несколько и выдаёт
 * рабочий; Яндекс отказал (403/429) — приложение сообщает об этом
 * (`POST /maps/key/exhausted`) и берёт ключ, который сервер выдаст в ответ.
 * Жалобе сервер не верит на слово: сам спрашивает Яндекс, не чаще раза в
 * 5 минут на ключ, так что лишняя жалоба ничего не ломает.
 *
 * Источник ключа общий для карты и HTTP-геокодера: ключ из кабинета Яндекса
 * открыт для обоих API.
 */
import type { ApiClient } from '../client'
import type { components } from '../generated/schema'

type MapKeyAnswerDto = components['schemas']['MapKeyAnswer']

export interface MapKeyAnswer {
  /** false — ни один ключ сегодня не работает: карту прячем. */
  available: boolean
  keyId: number | null
  key: string
}

export function toMapKeyAnswer(dto: MapKeyAnswerDto): MapKeyAnswer {
  const key = dto.key ?? ''
  return {
    available: (dto.available ?? false) && key !== '',
    keyId: dto.key_id ?? null,
    key,
  }
}

export async function fetchMapKey(api: ApiClient): Promise<MapKeyAnswer> {
  return toMapKeyAnswer(await api.get<MapKeyAnswerDto>('/maps/key'))
}

export async function reportMapKeyExhausted(api: ApiClient, keyId: number): Promise<MapKeyAnswer> {
  return toMapKeyAnswer(await api.post<MapKeyAnswerDto>('/maps/key/exhausted', { key_id: keyId }))
}

/**
 * Откуда карта и геокодер берут ключ. Тот же контракт описан у загрузчика
 * карт в `@dorixona/ui`; пакеты друг от друга не зависят.
 */
export interface MapKeySource {
  /** Ключ, с которым грузить; пустая строка — ключа нет. */
  key(): Promise<string>
  /** Яндекс не пустил с этим ключом — ключ, который пробовать теперь; '' — пробовать нечего. */
  refused(key: string): Promise<string>
}

/** Ключ из сборки: запасного нет. */
export function staticMapKeySource(key: string): MapKeySource {
  return {
    key: () => Promise.resolve(key),
    refused: () => Promise.resolve(''),
  }
}

/**
 * Ключ из пула. Запрос за ключом — один на страницу: и карта, и геокодер
 * берут один ответ. Запасного ключа нет: пул пуст или сервер не ответил —
 * ключа нет, карта показывает ошибку. Сбой сети не запоминаем — следующая
 * карта спросит снова.
 */
export function createPoolMapKeySource(api: ApiClient): MapKeySource {
  let current: Promise<MapKeyAnswer> | null = null
  const none: MapKeyAnswer = { available: false, keyId: null, key: '' }

  const keyOf = (answer: MapKeyAnswer) => (answer.available ? answer.key : '')
  const forget = () => {
    current = null
    return none
  }

  function load(): Promise<MapKeyAnswer> {
    current ??= fetchMapKey(api).catch(forget)
    return current
  }

  return {
    key: async () => keyOf(await load()),
    refused: async (key) => {
      const answer = await load()
      // Жалоба на ключ, который уже заменён, или ключ без номера — жаловаться нечем.
      if (!answer.available || answer.key !== key || answer.keyId === null) return ''
      current = reportMapKeyExhausted(api, answer.keyId).catch(forget)
      return keyOf(await current)
    },
  }
}

export interface MapKeyConfig {
  /** Пул ключей включён (`VITE_FEATURE_MAPS_KEYS`): ключ только с сервера. */
  pool: boolean
  /** Ключ из сборки (`VITE_YANDEX_MAPS_KEY`): им грузимся, пока пул выключен. */
  buildKey: string
}

const sources = new WeakMap<ApiClient, MapKeySource>()
const statics = new Map<string, MapKeySource>()

/**
 * Источник ключа для клиента API. Один и тот же объект на каждый вызов: все
 * карты страницы делят один ответ сервера, а экран может держать источник в
 * зависимостях эффекта. Пул выключен — ключ из сборки, как раньше;
 * включён — только пул, ключ сборки не подставляется.
 */
export function mapKeySourceFor(api: ApiClient, config: MapKeyConfig): MapKeySource {
  if (!config.pool) {
    let source = statics.get(config.buildKey)
    if (!source) {
      source = staticMapKeySource(config.buildKey)
      statics.set(config.buildKey, source)
    }
    return source
  }
  let source = sources.get(api)
  if (!source) {
    source = createPoolMapKeySource(api)
    sources.set(api, source)
  }
  return source
}

export {
  GEOCODER_URL,
  geocoderLang,
  reverseGeocode,
  searchAddresses,
  type GeoAddress,
} from './geocoder'
